import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import Spinner from 'sushiweb/Spinner';
import HorizontalTabs from 'sushiweb/HorizontalTabs';
import red from 'sushiweb/tokens/color/red';
import Breadcrumb from 'sushiweb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';

import {
  applicableFlowTypes,
  currentFlowTypeEnum,
  GIFTING,
  programTypeEnum,
  REFRESH_LABEL,
} from '../common/components/constants';
import { convertStringToInteger } from '../../helpers';
import {
  BreadCrumbWrapper,
  DashboardWrapper,
  SpinnerHolder,
  StyledButton,
  TabContainer,
  TabHeadWrapper,
  tabStyle,
  TabWrapper,
} from '../common/styles';

import GiftingAdhocPlanTable from './GiftingAdhocPlanTable';
import ErrorPage from '../common/components/ErrorPage';
import EmptyStateV2 from '../common/components/EmptyStateV2/EmptyStateV2';
import { emptyDashboardStateTitle } from '../../constants';
import Banner from '../common/components/Banner';
import { DASHBOARD_ROUTE, HOME_ROUTE } from '../../constants/routes';
import CreateProgramModal from '../WalletPrograms/CreateProgramModal';
import { GC_PLAN } from '../WalletPrograms/CreateProgramModal/ProgramForms/common/Components/constants';
import GiftingRecipientsTable from './GiftingRecipientsTable';
import ZTrackingClient from '../../jumbo/ZTrackingClient';

const AdhocPrograms = ({
  currentFlow,
  viewOnlyMode,
  fetchingPrograms,
  fetchedPrograms,
  programsData,
  getAllPrograms,
  selectedWorkspace,
}) => {
  localStorage.activeIndexGifting =
    localStorage.activeIndexGifting !== undefined
      ? Number(localStorage.activeIndexGifting)
      : 0;

  const { programs = [] } = programsData;
  const [createPlanModal, setCreatePlanModal] = useState(false);
  const [activeIndexGifting, setActiveIndexGifting] = useState(
    localStorage.activeIndexGifting !== undefined
      ? Number(localStorage.activeIndexGifting)
      : 0,
  );

  const handleTabsClick = val => () => {
    setActiveIndexGifting(val);
    localStorage.activeIndexGifting = Number(val);
    const comp = val === 0 ? 'GIFTING_HISTORY_TAB' : 'GIFTING_RECIPIENTS_TAB';
    ZTrackingClient.recordClick({
      ui_source: 'GIFTING',
      component: comp,
    });
  };

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'GIFTING',
      component: 'SIDE_NAV_BAR',
    });
  }, []);

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getAllPrograms({
        workspaceId: selectedWorkspace.workspace_id ?? '',
        flowType: convertStringToInteger(currentFlow),
        programType: programTypeEnum.PROGRAM_TYPE_ADHOC,
      });
  }, [selectedWorkspace, currentFlow]);

  const showSelectedTable = () => {
    if (activeIndexGifting === 0) return <GiftingAdhocPlanTable />;
    return <GiftingRecipientsTable />;
  };

  const closeCreateGroupModal = () => {
    setCreatePlanModal(false);
    getAllPrograms({
      workspaceId: selectedWorkspace.workspace_id ?? '',
      flowType: convertStringToInteger(currentFlow),
      programType: programTypeEnum.PROGRAM_TYPE_ADHOC,
    });
  };
  const openCreatePlanModal = () => {
    setCreatePlanModal(true);
    ZTrackingClient.recordClick({
      ui_source: 'GIFTING',
      component: 'SEND_EGIFT_CARD_BUTTON',
    });
  };

  const handleActiveStateButtonClick = () => {
    if (activeIndexGifting === 0) {
      openCreatePlanModal();
    }
    if (activeIndexGifting === 1) {
      openCreatePlanModal();
    }
  };

  const activeStateMapping = {
    0: {
      buttonText: 'Send gift card',
    },
    1: {
      buttonText: 'Send gift card',
    },
  };

  const refreshPage = () => {
    getAllPrograms({
      workspaceId: selectedWorkspace.workspace_id ?? '',
      flowType: convertStringToInteger(currentFlow),
      programType: programTypeEnum.PROGRAM_TYPE_ADHOC,
    });
  };

  const ActionComponent = () => {
    return (
      <div>
        <StyledButton onClick={openCreatePlanModal} disabled={viewOnlyMode}>
          Send gift card
        </StyledButton>
      </div>
    );
  };

  if (fetchingPrograms) {
    return (
      <DashboardWrapper>
        <SpinnerHolder className='center'>
          <Spinner color={red.z_red} size='medium' />
        </SpinnerHolder>
      </DashboardWrapper>
    );
  }

  if (!fetchedPrograms) {
    return (
      <DashboardWrapper>
        <ErrorPage
          showAction
          actionLabel={REFRESH_LABEL}
          clickAction={refreshPage}
        />
      </DashboardWrapper>
    );
  }

  return !programs || programs.length === 0 ? (
    <>
      {createPlanModal && (
        <CreateProgramModal
          title={GC_PLAN}
          showModal={createPlanModal}
          closeModal={closeCreateGroupModal}
        />
      )}
      <EmptyStateV2
        state={GIFTING}
        title={emptyDashboardStateTitle}
        actionComponent={ActionComponent}
      />
    </>
  ) : (
    <DashboardWrapper>
      {createPlanModal && (
        <CreateProgramModal
          title={GC_PLAN}
          showModal={createPlanModal}
          closeModal={closeCreateGroupModal}
        />
      )}
      {viewOnlyMode && <Banner />}
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
          >
            Home
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
          >
            Gifting
          </Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <TabHeadWrapper>
        <H5>Summary</H5>
        <div>
          <StyledButton
            onClick={handleActiveStateButtonClick}
            disabled={viewOnlyMode}
          >
            {activeStateMapping[activeIndexGifting]?.buttonText}
          </StyledButton>
        </div>
      </TabHeadWrapper>

      <TabWrapper>
        <TabContainer>
          <HorizontalTabs
            style={{
              width: 'auto',
              marginBottom: '-4.5px',
              marginLeft: '10px',
            }}
            alignLinks='left'
            additionalGap='1.8rem'
          >
            <HorizontalTabs.Link
              active={activeIndexGifting === 0}
              onClick={handleTabsClick(0)}
              style={{
                width: '160px',
                color:
                  activeIndexGifting === 0
                    ? 'var(--coolgreycoolgrey-800, #414A5D)'
                    : 'var(--coolgreycoolgrey-400, #AFB4C0)',
                ...tabStyle,
              }}
            >
              Gift card history
            </HorizontalTabs.Link>
            {/* <HorizontalTabs.Link
              active={activeIndexGifting === 1}
              onClick={handleTabsClick(1)}
              style={{
                width: '140px',
                color:
                  activeIndexGifting === 1
                    ? 'var(--coolgreycoolgrey-800, #414A5D)'
                    : 'var(--coolgreycoolgrey-400, #AFB4C0)',
                ...tabStyle,
              }}
            >
              Recipients
            </HorizontalTabs.Link> */}
          </HorizontalTabs>
        </TabContainer>
        {showSelectedTable()}
      </TabWrapper>
    </DashboardWrapper>
  );
};

AdhocPrograms.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  programsData: PropTypes.objectOf(PropTypes.any),
  getAllPrograms: PropTypes.func,
  fetchedPrograms: PropTypes.bool,
  fetchingPrograms: PropTypes.bool,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  viewOnlyMode: PropTypes.bool,
};

AdhocPrograms.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD,
  programsData: {},
  getAllPrograms: _noop,
  fetchedPrograms: true,
  fetchingPrograms: true,
  selectedWorkspace: {},
  viewOnlyMode: false,
};

export default AdhocPrograms;
